"use client";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Button } from "@/src/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/src/components/ui/form";
import { Input } from "@/src/components/ui/input";
import { Checkbox } from "@/src/components/ui/checkbox";
import { toast } from "@/src/hooks/use-toast";
import {
  Lock,
  Mail,
  Eye,
  EyeOff,
  ArrowRight,
  ShieldCheck,
  LifeBuoy,
  X,
  CircleHelp,
} from "lucide-react";
import { useRouter } from "@/src/i18n/routing";
import { signIn } from "next-auth/react";
import { useTranslations } from "next-intl";
import { useState } from "react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/src/components/ui/tooltip";

// Esquema de validación - se crea dentro del componente para usar traducciones
const createFormSchema = (t: (key: string) => string) =>
  z.object({
    userIdentifier: z.string(),
    password: z.string().min(8, t("passwordMinError")),
    rememberMe: z.boolean().default(false),
  });
interface LoginProps {
  isLoading: boolean;
  setIsLoading: (value: boolean) => void;
}
const Login = ({ isLoading, setIsLoading }: LoginProps) => {
  const [showPassword, setShowPassword] = useState(false);
  const [showSupport, setShowSupport] = useState(false);

  const t = useTranslations("Login");
  const auth = useTranslations("AuthNotify");

  const formSchema = createFormSchema(t);

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema) as any,
    defaultValues: {
      userIdentifier: "",
      password: "",
      rememberMe: false,
    },
  });

  async function onSubmit(values: z.infer<typeof formSchema>) {
    setIsLoading(true);
    const authResp = await signIn("credentials", {
      userId: values.userIdentifier,
      password: values.password,
      rememberMe: values.rememberMe,
      redirect: false,
    });

    if (authResp?.error) {
      toast({
        title: auth("title"),
        description: auth("credentialsInvalid"),
        variant: "error",
      });
    } else if (authResp?.ok) {
      toast({
        title: auth("title"),
        description:
          auth("welcome") +
          ": " +
          values.userIdentifier.split("@")[0].toLowerCase(),
        variant: "success",
      });
    }

    setIsLoading(false);
  }
  return (
    <div className="w-full max-w-sm space-y-6 rounded-2xl border border-slate-200/80 bg-white p-7 shadow-xl shadow-slate-950/10 dark:border-slate-800 dark:bg-slate-950 sm:p-8">
      <div className="flex items-start justify-between">
        <div>
          <div className="mb-4 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-teal-700 dark:text-teal-300">
            <span className="h-1.5 w-1.5 rounded-full bg-teal-500" />
            UGAT Plataforma
          </div>
          <div className="mt-4 h-px w-24 bg-teal-600" />
        </div>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
          <FormField
            control={form.control}
            name="userIdentifier"
            render={({ field }) => (
              <FormItem>
                <div className="flex items-center gap-1.5">
                  <FormLabel>{t("email")}</FormLabel>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          type="button"
                          aria-label={t("loginValueHelp")}
                          className="text-muted-foreground transition-colors hover:text-teal-700 dark:hover:text-teal-300"
                        >
                          <CircleHelp className="h-4 w-4" />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent className="max-w-xs leading-relaxed">
                        {t("loginValueHelp")}
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
                <FormControl>
                  <div className="relative">
                    <Mail className="absolute left-3 top-2.5 h-5 w-5 text-muted-foreground" />
                    <Input
                      placeholder={t("emailPlaceholder")}
                      className="pl-10"
                      {...field}
                    />
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("password")}</FormLabel>
                <FormControl>
                  <div className="relative">
                    <Lock className="absolute left-3 top-2.5 h-5 w-5 text-muted-foreground" />
                    <Input
                      type={showPassword ? "text" : "password"}
                      placeholder="••••••••"
                      className="pl-10 pr-10"
                      {...field}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-2.5 text-muted-foreground hover:text-foreground transition-colors"
                    >
                      {showPassword ? (
                        <EyeOff className="h-5 w-5" />
                      ) : (
                        <Eye className="h-5 w-5" />
                      )}
                    </button>
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="rememberMe"
            render={({ field }) => (
              <FormItem className="flex items-center space-x-2">
                <FormControl>
                  <Checkbox
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
                <FormLabel className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                  {t("remberMe")}
                </FormLabel>
              </FormItem>
            )}
          />

          <Button
            type="submit"
            className="w-full h-11 rounded-xl bg-teal-700 text-white shadow-lg shadow-teal-700/20 transition-all hover:bg-teal-800 hover:shadow-teal-700/30"
            disabled={isLoading}
          >
            {isLoading ? (
              t("submitLoading")
            ) : (
              <>
                {t("submit")}
                <ArrowRight className="ml-2 h-4 w-4" />
              </>
            )}
          </Button>
        </form>
      </Form>
      <div className="border-t border-slate-200 pt-5 text-center dark:border-slate-800">
        <p className="text-xs text-slate-500">
          ¿Necesitas ayuda para ingresar?
        </p>
        <button
          type="button"
          onClick={() => setShowSupport(true)}
          className="mt-2 inline-flex items-center text-sm font-medium text-teal-700 hover:text-teal-800 dark:text-teal-300"
        >
          <LifeBuoy className="mr-1.5 h-4 w-4" /> Contactar soporte
        </button>
      </div>
      {showSupport && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-950/50 p-4 text-left">
          <form
            className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-6 shadow-2xl dark:border-slate-800 dark:bg-slate-950"
            onSubmit={(event) => {
              event.preventDefault();
              window.location.href = `mailto:soporte@cloudtek.cl?subject=Soporte de acceso&body=${encodeURIComponent((event.currentTarget.elements.namedItem("reason") as HTMLSelectElement).value + "\n\n" + (event.currentTarget.elements.namedItem("message") as HTMLTextAreaElement).value)}`;
            }}
          >
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-teal-700">
                  Soporte
                </p>
                <h2 className="mt-1 text-xl font-semibold text-slate-900 dark:text-white">
                  ¿Cómo podemos ayudarte?
                </h2>
                <p className="mt-1 text-sm text-slate-500">
                  Cuéntanos qué necesitas y te contactaremos.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setShowSupport(false)}
                className="text-slate-400 hover:text-slate-700"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="mt-5 space-y-4">
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-200">
                Motivo
                <select
                  name="reason"
                  className="mt-1.5 flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                >
                  <option>No puedo ingresar</option>
                  <option>Quiero ser contactado</option>
                  <option>Otro</option>
                </select>
              </label>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-200">
                Mensaje
                <textarea
                  name="message"
                  required
                  rows={4}
                  className="mt-1.5 w-full rounded-md border border-input bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-teal-600"
                  placeholder="Describe brevemente tu solicitud"
                />
              </label>
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowSupport(false)}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                className="bg-teal-700 text-white hover:bg-teal-800"
              >
                Enviar solicitud
              </Button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default Login;
